import { ChevronRight, Copy } from '@carbon/icons-react';
import {
  CustomizedButton,
  CustomizedTypography,
  Stack,
  VStack
} from '@components/Customized';
import { colorSet, useSnackbar } from '@components/Provider';
import {
  CapaPaymentCancelStatus,
  CapaPaymentMethodType,
  CapaPurchaseOrderModel,
  CapaPurchaseOrderStatus,
  CapaQuotationServiceType,
  Maybe,
  useFetchMyRefundBankAccountsQuery
} from '@generated/graphql';
import {
  Avatar,
  Badge,
  Box,
  Divider,
  Theme,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { formatDate } from '@utils/formatter';
import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { CancelReasonModal, ExtensionPaymentDateModal } from '../../Modal';
import Image from '@components/Image/Image';
import { ButtonByStatus } from '@components/Form/PurchaseOrder/PurchaseOrder/BottomButtonGroup';
import { processEvents } from '@utils/customEvent';
import { bankMap } from '@utils/bankMap';
import { CapaBankCodeToBankInfo } from '@routes/MyAccount/components/BankAccount/BankAccountInputGroup/BankAccountInputGroup';
import AppConfig from '@config';
import {
  paymentMethodEnumToString,
  requestEnumToString
} from '@utils/enumToString';
import { maskingBankAccount } from '@utils/maskingString';
import * as assets from '@assets';
import { FallbackCard } from '@routes/Payment/Payment';
import {
  ClientProcessStateToDescriptionMap,
  ProcessStateToTitleMap,
  purchaseOrderStatusToKorean
} from '@components/Form/PurchaseOrder/PurchaseOrder/ProgressInfoBox/constant';
import { ProcessStateEnum } from '@routes/Payment/types';
import DeliveryExtendReasonModal from '../../Modal/DeliveryExtendReasonModal';

const EXCEPTIONAL_PROCESS_STATES = [
  CapaPurchaseOrderStatus.CancelRequested,
  CapaPurchaseOrderStatus.CancelConfirmed,
  CapaPurchaseOrderStatus.CancelledByAdmin,
  CapaPurchaseOrderStatus.RefundRequested,
  CapaPurchaseOrderStatus.RefundConfirmed,
  CapaPurchaseOrderStatus.RejectOrderByPartner,
  CapaPurchaseOrderStatus.NotPaidCanceled
];

const refundRequestedStatus = [
  CapaPurchaseOrderStatus.RefundRequested,
  CapaPurchaseOrderStatus.CancelRequested
];

const PaymentItemCard = forwardRef<HTMLDivElement, CapaPurchaseOrderModel>(
  (
    {
      purchaseOrderStatus,
      serviceType,
      partnerFullAddress,
      partnerLogoFileId,
      partnerManagerName,
      partnerOrganizationName,
      projectName,
      purchaseOrderId,
      quantity,
      rfqCreatedAt,
      paymentManageModelList
    },
    ref
  ) => {
    const {
      paymentMethod,
      rfqId,
      quotationPartnerId,
      virtualAccountNumber,
      virtualBankCode,
      cardNumber,
      totalAmount,
      paidAt,
      registerExpectedDate,
      cancelStatus,
      hasReview,
      chatRoomId,
      cancelReason,
      cancelAmount,
      thumbnailId,
      cancelConfirmedAt,
      cardName,
      extendReason,
      confirmedAt,
      extendedPurchaseConfirmDate
    } = paymentManageModelList![0];

    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down('md'), {
      noSsr: true
    });
    const [isOpen, setIsOpen] = useState(false);
    const isExepctionalState = EXCEPTIONAL_PROCESS_STATES.includes(
      purchaseOrderStatus
    );
    const { enqueueSnackbar } = useSnackbar();
    const classes = useStyles();
    const { data, loading } = useFetchMyRefundBankAccountsQuery({
      fetchPolicy: 'cache-first'
    });

    const openReviewModal = useCallback(() => {
      processEvents.리뷰작성({
        rfqId: rfqId ?? '',
        quotationPartnerId: quotationPartnerId ?? '',
        partnerProfileLogoId: partnerLogoFileId ?? '',
        projectName: projectName ?? '',
        organizationName: partnerOrganizationName ?? ''
      });
    }, [
      partnerOrganizationName,
      projectName,
      quotationPartnerId,
      rfqId,
      partnerLogoFileId
    ]);

    const koreaStatus =
      extendReason && purchaseOrderStatus === CapaPurchaseOrderStatus.Shipping
        ? '구매확정일연기'
        : purchaseOrderStatusToKorean[purchaseOrderStatus] ??
          ProcessStateEnum.계좌이체대기;

    const statusBadge = useMemo(() => {
      const label = ProcessStateToTitleMap[koreaStatus];

      return (
        <Box
          bgcolor={
            isExepctionalState
              ? colorSet.errorLight
              : label === '거래완료'
              ? colorSet.primary500
              : colorSet.primary200
          }
          borderRadius={100}
          padding="4px 8px"
          color={colorSet.gray000}
        >
          <CustomizedTypography
            variant="caption"
            color={
              isExepctionalState
                ? 'error'
                : label === '거래완료'
                ? 'inherit'
                : 'primary'
            }
          >
            {label}
          </CustomizedTypography>
        </Box>
      );
    }, [isExepctionalState, koreaStatus]);

    if (loading) {
      return <FallbackCard />;
    }

    const refundAccount =
      data?.fetchMyRefundBankAccounts.data?.[0]?.account ?? '정보없음';
    const refundbankName = bankMap(
      data?.fetchMyRefundBankAccounts.data?.[0]?.bankCode
    );

    const paymentInfoLabel =
      paymentMethod === CapaPaymentMethodType.CreditCard
        ? `${(cardName ?? '').replace('카드', '')} ${maskingBankAccount(
            cardNumber
          )}`
        : `환불계좌: ${refundbankName} ${maskingBankAccount(refundAccount)}`;

    const handleCopy = async () => {
      const bankName = CapaBankCodeToBankInfo[virtualBankCode!]?.name ?? '';

      try {
        await navigator?.clipboard?.writeText?.(
          `${bankName} ${virtualAccountNumber}`
        );
        enqueueSnackbar('계좌 번호 복사에 성공했습니다.');
      } catch (error) {
        enqueueSnackbar('계좌 번호 복사에 실패했습니다.', { variant: 'error' });
      }
    };

    const totalAmountToLocaleString = totalAmount?.toLocaleString('ko') ?? '';
    const formattedPaidDate = paidAt ? formatDate(paidAt, 'YYYY.MM.DD') : null;
    const formattedCancelConfirmeDate = cancelConfirmedAt
      ? formatDate(cancelConfirmedAt, 'YYYY.MM.DD')
      : null;
    const formattedconfirmedDate = confirmedAt
      ? formatDate(confirmedAt, 'YYYY.MM.DD')
      : null;
    const cancelLabel = getCancelLabel(cancelStatus);
    const buttonDisabled =
      refundRequestedStatus.includes(purchaseOrderStatus) || hasReview;
    const formattedRfqCreatedDate = formatDate(rfqCreatedAt!, 'YYYY.MM.DD');
    const logoSrc = partnerLogoFileId
      ? `${AppConfig.fileApiUrl}/file/${partnerLogoFileId}`
      : '';
    const isPcbDesign =
      serviceType === CapaQuotationServiceType.Pcb && !thumbnailId;
    const thumbnailUrl =
      CapaQuotationServiceType.Design === serviceType
        ? `${AppConfig.fileApiUrl}/file/${thumbnailId}`
        : `${AppConfig.fileApiUrl}/cloud/${thumbnailId}.png`;
    const thumbnailSrc = isPcbDesign
      ? assets.PCBDesignIcon
      : thumbnailId
      ? thumbnailUrl
      : assets.ThumbnailDefaultImg;

    const paymentDateLabel = getPaymentDateLabel(purchaseOrderStatus);

    return (
      <div className={classes.cardContainer} ref={ref}>
        <div className={classes.paymentBox}>
          {matches && <Box width="fit-content">{statusBadge}</Box>}
          <div className={classes.projectInfo}>
            <Badge
              className={classes.badge}
              anchorOrigin={{
                horizontal: 'right',
                vertical: 'bottom'
              }}
              badgeContent={
                <Avatar
                  src={logoSrc}
                  style={{
                    border: `1px solid ${colorSet.gray000}`,
                    width: 44,
                    height: 44
                  }}
                />
              }
            >
              <div className="thumbnail">
                <Image
                  src={thumbnailSrc}
                  width={isPcbDesign ? 70 : '100%'}
                  height={isPcbDesign ? 70 : '100%'}
                  alt="thumbnail"
                  onError={(e) => {
                    e.currentTarget.src = assets.ThumbnailDefaultImg;
                    e.currentTarget.style.backgroundColor = '#fff';
                  }}
                />
              </div>
            </Badge>

            <div className="project-sumarry">
              <Stack justifyContent="space-between" alignItems="center">
                <CustomizedTypography variant="body1" fontWeight="500" noWrap>
                  {projectName ?? ''}
                </CustomizedTypography>
                {!matches && statusBadge}
              </Stack>
              <CustomizedTypography variant="body2" color="textSecondary">
                요청서 작성일 {formattedRfqCreatedDate}
              </CustomizedTypography>
              <CustomizedTypography
                variant="body2"
                color="textSecondary"
                display="inline"
              >
                {requestEnumToString(serviceType)}
              </CustomizedTypography>
              {quantity && (
                <>
                  <CustomizedTypography
                    variant="body2"
                    display="inline"
                    color="textSecondary"
                  >
                    {' '}
                    | 수량{' '}
                    <em className="text-primary">
                      {quantity.toLocaleString('kr')}
                    </em>
                  </CustomizedTypography>
                  <CustomizedTypography
                    variant="body2"
                    color="textSecondary"
                    display="inline"
                  >
                    개
                  </CustomizedTypography>
                </>
              )}

              <Box
                display="flex"
                flexDirection="column"
                borderRadius={8}
                marginTop={1}
                bgcolor={colorSet.gray100}
                padding="8px 12px 8px 12px"
                style={{
                  cursor: 'pointer'
                }}
                onClick={() => {
                  window.open(
                    `${AppConfig.baseUrl}/companyInfo?partnerId=${quotationPartnerId}`
                  );
                }}
              >
                <CustomizedTypography variant="body2" fontWeight="500">
                  {partnerManagerName} ({partnerOrganizationName})
                </CustomizedTypography>
                <CustomizedTypography
                  variant="body2"
                  color="textSecondary"
                  noWrap
                >
                  {partnerFullAddress}
                </CustomizedTypography>
              </Box>
            </div>
          </div>
          <Divider />
          <div>
            {matches ? (
              <VStack>
                <Stack justifyContent="space-between">
                  {formattedPaidDate && (
                    <>
                      <CustomizedTypography
                        variant="body2"
                        fontWeight="500"
                        gutterBottoms={2}
                      >
                        결제 완료일
                      </CustomizedTypography>
                      <CustomizedTypography
                        variant="body2"
                        fontWeight="500"
                        gutterBottoms={2}
                      >
                        {formattedPaidDate}
                      </CustomizedTypography>
                    </>
                  )}
                </Stack>
                <Stack justifyContent="space-between">
                  <CustomizedTypography
                    variant="body2"
                    fontWeight="500"
                    gutterBottoms={2}
                    style={{
                      minWidth: 130
                    }}
                  >
                    결제 수단
                  </CustomizedTypography>
                  <CustomizedTypography
                    variant="body2"
                    fontWeight="500"
                    gutterBottoms={2}
                    noWrap
                  >
                    {paymentMethodEnumToString(paymentMethod)} (
                    {paymentInfoLabel})
                  </CustomizedTypography>
                </Stack>
                <Stack justifyContent="space-between">
                  <CustomizedTypography variant="body1" fontWeight="500">
                    총 결제금액
                  </CustomizedTypography>
                  <CustomizedTypography
                    color={formattedPaidDate ? 'primary' : 'textSecondary'}
                    variant="body1"
                    fontWeight="bold"
                    style={
                      isCanceledStatus(purchaseOrderStatus)
                        ? {
                            textDecoration: 'line-through',
                            color: colorSet.gray500
                          }
                        : {}
                    }
                  >
                    {totalAmountToLocaleString} 원
                  </CustomizedTypography>
                </Stack>
              </VStack>
            ) : (
              <>
                <CustomizedTypography variant="subtitle2">
                  {paymentMethodEnumToString(paymentMethod)} ({paymentInfoLabel}
                  )
                </CustomizedTypography>
                <CustomizedTypography
                  variant="body2"
                  color={formattedPaidDate ? 'primary' : 'textSecondary'}
                  fontWeight="bold"
                  display="inline"
                  style={
                    isCanceledStatus(purchaseOrderStatus)
                      ? {
                          textDecoration: 'line-through',
                          color: colorSet.gray500
                        }
                      : {}
                  }
                >
                  {totalAmountToLocaleString}원
                </CustomizedTypography>
                {formattedPaidDate ? (
                  <CustomizedTypography variant="body2" display="inline">
                    {' '}
                    (결제 완료일: {formattedPaidDate}
                    {paymentDateLabel}
                    {formattedconfirmedDate || formattedCancelConfirmeDate})
                  </CustomizedTypography>
                ) : (
                  <CustomizedTypography variant="body2" display="inline">
                    {' '}
                    (결제대기)
                  </CustomizedTypography>
                )}
              </>
            )}
          </div>
        </div>
        <div className={classes.processBox}>
          <div className={classes.processDetail}>
            <div className="process-state">
              <CustomizedTypography
                variant="body1"
                color={isExepctionalState ? 'error' : 'primary'}
              >
                • &nbsp;
              </CustomizedTypography>
              <CustomizedTypography
                variant="body1"
                color="textPrimary"
                fontWeight="500"
              >
                {ProcessStateToTitleMap[koreaStatus]}
              </CustomizedTypography>

              {cancelLabel && (
                <CustomizedButton
                  variant="text"
                  color="default"
                  endIcon={<ChevronRight size={16} color={colorSet.gray700} />}
                  onClick={() => setIsOpen(true)}
                >
                  <CustomizedTypography
                    color="textSecondary"
                    variant="subtitle2"
                  >
                    {cancelLabel} 사유 보기
                  </CustomizedTypography>
                </CustomizedButton>
              )}

              {purchaseOrderStatus === CapaPurchaseOrderStatus.Shipping &&
                !extendReason && (
                  <ExtensionPaymentDateModal
                    expectedPaymentApproveDate={registerExpectedDate ?? ''}
                    paymentApproveDateAfterExtended={
                      extendedPurchaseConfirmDate ?? ''
                    }
                    purchaseOrderId={purchaseOrderId ?? ''}
                    rfqId={rfqId}
                    quotationPartnerId={quotationPartnerId}
                    openButton={(onClick) => (
                      <CustomizedButton
                        onClick={onClick}
                        variant="text"
                        endIcon={
                          <ChevronRight size={16} color={colorSet.gray700} />
                        }
                      >
                        <CustomizedTypography
                          color="textSecondary"
                          variant="subtitle2"
                        >
                          자동 구매확정일 연장
                        </CustomizedTypography>
                      </CustomizedButton>
                    )}
                  />
                )}
              {purchaseOrderStatus === CapaPurchaseOrderStatus.Shipping &&
                extendReason && (
                  <CustomizedButton
                    variant="text"
                    color="default"
                    endIcon={
                      <ChevronRight size={16} color={colorSet.gray700} />
                    }
                    onClick={() => setIsOpen(true)}
                  >
                    <CustomizedTypography
                      color="textSecondary"
                      variant="subtitle2"
                    >
                      구매확정일 연장 사유
                    </CustomizedTypography>
                  </CustomizedButton>
                )}
            </div>
            <CustomizedTypography variant="body2" className="state-description">
              {ClientProcessStateToDescriptionMap[koreaStatus]}
            </CustomizedTypography>
            {CapaPurchaseOrderStatus.ProcessPayment === purchaseOrderStatus && (
              <Box
                display="flex"
                justifyContent="space-between"
                marginTop={matches ? '12px' : '24px'}
              >
                <Box display="flex" alignItems="center">
                  <Image
                    width={24}
                    height={24}
                    className="bank-logo"
                    src={CapaBankCodeToBankInfo[virtualBankCode!]?.logo ?? ''}
                    style={{
                      marginRight: 9
                    }}
                  />
                  <CustomizedTypography
                    variant="body2"
                    fontWeight="500"
                    display="inline"
                    style={{
                      marginRight: 4
                    }}
                  >
                    (주) 에이팀벤처스
                  </CustomizedTypography>
                  <CustomizedTypography
                    variant="body2"
                    display="inline"
                    style={{
                      marginRight: 2
                    }}
                  >
                    {CapaBankCodeToBankInfo[virtualBankCode!]?.name ?? ''}
                  </CustomizedTypography>
                  <CustomizedTypography
                    variant="body2"
                    color="textSecondary"
                    display="inline"
                    style={{
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                    onClick={handleCopy}
                  >
                    {virtualAccountNumber}
                  </CustomizedTypography>
                </Box>
                <Copy
                  onClick={handleCopy}
                  size={20}
                  style={{ cursor: 'pointer' }}
                />
              </Box>
            )}
          </div>
          <div className={classes.buttonGroup}>
            <ButtonByStatus
              buttonProps={{
                fullWidth: true,
                disabled: buttonDisabled
              }}
              orderStatus={purchaseOrderStatus}
              chatRoomId={chatRoomId ?? ''}
              quotationPartnerId={quotationPartnerId}
              rfqId={rfqId}
              totalAmount={totalAmount}
              purchaseOrderId={purchaseOrderId ?? ''}
              openReviewModal={openReviewModal}
              registerExpectedDate={registerExpectedDate ?? ''}
              isExtended={!!extendReason}
            />
          </div>
        </div>
        {cancelStatus && (
          <CancelReasonModal
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            cancelStatus={cancelStatus as CapaPaymentCancelStatus}
            cancelReason={cancelReason ?? ''}
            refoundPrice={cancelAmount ?? 0}
          />
        )}
        {extendReason && (
          <DeliveryExtendReasonModal
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            extendReason={extendReason ?? ''}
            registerExpectedDate={registerExpectedDate ?? ''}
          />
        )}
      </div>
    );
  }
);

const useStyles = makeStyles<Theme>((theme) => ({
  cardContainer: {
    display: 'flex',
    border: `1px solid ${colorSet.gray300}`,
    borderRadius: '12px',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      minWidth: '343px'
    },
    '& * .text-primary': {
      color: colorSet.primary500
    }
  },
  paymentBox: {
    padding: '24px',
    width: '100%',
    minWidth: 550,
    '& hr': {
      margin: '16px 0px 16px 0px',
      backgroundColor: colorSet.gray300
    },
    [theme.breakpoints.down('md')]: {
      minWidth: 'unset'
    }
  },
  projectInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    '& b': {
      fontWeight: 500
    },
    '& .thumbnail': {
      minWidth: 100,
      minHeight: 110,
      width: 100,
      height: 110,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      border: `1px solid ${colorSet.gray400}`,
      backgroundColor: colorSet.gray100,
      borderRadius: 16,
      '& > img': {
        maxWidth: 100,
        maxHeight: 110,
        borderRadius: 16,
        objectFit: 'contain'
      }
    },
    '& .project-sumarry': {
      width: '100%',
      overflow: 'hidden'
    }
  },
  paymentDetail: {
    display: 'flex',
    gap: '24px',
    '& > div': {
      width: '100%',
      maxWidth: 243
    },
    '& .commission': {
      display: 'flex',
      alignItems: 'center',
      gap: 2
    },
    '& .amount': {
      marginLeft: 'auto',
      textAlign: 'right',
      [theme.breakpoints.down('md')]: {
        marginLeft: 'unset'
      }
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: '8px',
      '& > div': {
        maxWidth: 'unset',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }
    }
  },
  processBox: {
    display: 'flex',
    gap: '24px',
    flexDirection: 'column',
    width: '100%',
    minWidth: 410,
    borderLeft: `1px dashed ${colorSet.gray300}`,
    padding: '24px',
    [theme.breakpoints.down('md')]: {
      minWidth: 'unset',
      borderLeft: 'unset',
      borderTop: `1px dashed ${colorSet.gray300}`
    }
  },
  processDetail: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    '& .process-state': {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '8px',
      '& button': {
        marginLeft: 'auto'
      },
      '& .MuiTypography-colorError': {
        color: colorSet.errorBase
      }
    }
  },
  badge: {
    '& .MuiBadge-badge': {
      right: 15,
      bottom: 20,
      '& > div': {
        boxShadow: `0px 8px 16px 2px rgba(97, 97, 97, 0.1), 0px 16px 32px 2px rgba(97, 97, 97, 0.1)`
      }
    }
  },
  buttonGroup: {
    display: 'flex',
    gap: '8px'
  }
}));

export default React.memo(PaymentItemCard);

const getCancelLabel = (cancelStatus?: Maybe<CapaPaymentCancelStatus>) => {
  if (CapaPaymentCancelStatus.RejectOrderByPartner === cancelStatus) {
    return '발주서 거절';
  }
  if (CapaPaymentCancelStatus.CancelRequested === cancelStatus) {
    return '취소';
  }
  if (CapaPaymentCancelStatus.RefundRequested === cancelStatus) {
    return '환불';
  }
  return null;
};

const getPaymentDateLabel = (purchaseStatus: CapaPurchaseOrderStatus) => {
  switch (purchaseStatus) {
    case CapaPurchaseOrderStatus.RefundConfirmed: {
      return ' | 결제 환불일: ';
    }
    case CapaPurchaseOrderStatus.RejectOrderByPartner:
    case CapaPurchaseOrderStatus.CancelledByAdmin:
    case CapaPurchaseOrderStatus.CancelConfirmed: {
      return ' | 결제 취소일: ';
    }
    case CapaPurchaseOrderStatus.PurchaseConfirmed: {
      return ' | 구매확정일: ';
    }
    default:
      return '';
  }
};

const isCanceledStatus = (purchaseOrderStatus: CapaPurchaseOrderStatus) =>
  purchaseOrderStatus === CapaPurchaseOrderStatus.RefundConfirmed ||
  purchaseOrderStatus === CapaPurchaseOrderStatus.CancelConfirmed ||
  purchaseOrderStatus === CapaPurchaseOrderStatus.RejectOrderByPartner ||
  purchaseOrderStatus === CapaPurchaseOrderStatus.NotPaidCanceled ||
  purchaseOrderStatus === CapaPurchaseOrderStatus.CancelledByAdmin;
